import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CalendarIcon, ClockIcon, PhoneIcon } from 'lucide-react';
interface RescheduleCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (date: string, time: string, notes: string) => void;
  leadName: string;
  currentStatus: string;
}
export function RescheduleCallModal({
  isOpen,
  onClose,
  onSubmit,
  leadName,
  currentStatus
}: RescheduleCallModalProps) {
  const [callDate, setCallDate] = useState('');
  const [callTime, setCallTime] = useState('');
  const [notes, setNotes] = useState('');
  // Calculate minimum date (3 days from now)
  const getMinDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 3);
    return date.toISOString().split('T')[0];
  };
  const handleSubmit = () => {
    if (!callDate || !callTime) {
      alert('Te rog selectează data și ora pentru următorul apel');
      return;
    }
    // Validate date is at least 3 days from now
    const selectedDate = new Date(callDate);
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 3);
    if (selectedDate < minDate) {
      alert('Următorul apel trebuie programat la minim 3 zile de la primul apel');
      return;
    }
    onSubmit(callDate, callTime, notes);
    // Reset form
    setCallDate('');
    setCallTime('');
    setNotes('');
  };
  const getStatusMessage = () => {
    switch (currentStatus) {
      case 'nu-colaboreaza':
        return 'Clientul nu colaborează momentan. Programează un follow-up pentru a verifica dacă situația s-a schimbat.';
      case 'comision-redus':
        return 'Comisionul oferit este sub standard. Programează un apel pentru renegociere.';
      default:
        return 'Programează următorul apel pentru acest lead.';
    }
  };
  return <AnimatePresence data-id="element-5403">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-5404" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-5405">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden" data-id="element-5406">
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-5" data-id="element-5407">
                <div className="flex items-center justify-between" data-id="element-5408">
                  <div className="flex items-center space-x-3" data-id="element-5409">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm" data-id="element-5410">
                      <PhoneIcon className="w-6 h-6" data-id="element-5411" />
                    </div>
                    <div data-id="element-5412">
                      <h3 className="text-xl font-bold" data-id="element-5413">Reprogramare Apel</h3>
                      <p className="text-orange-100 text-sm" data-id="element-5414">{leadName}</p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors" data-id="element-5415">
                    <XIcon className="w-6 h-6" data-id="element-5416" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6" data-id="element-5417">
                {/* Info Message */}
                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-xl" data-id="element-5418">
                  <p className="text-sm text-orange-900" data-id="element-5419">
                    <strong data-id="element-5420">ℹ️ Notă:</strong> {getStatusMessage()}
                  </p>
                </div>

                {/* Date Input */}
                <div data-id="element-5421">
                  <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-5422">
                    Data Următorului Apel *
                  </label>
                  <div className="relative" data-id="element-5423">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" data-id="element-5424" />
                    <input type="date" value={callDate} onChange={e => setCallDate(e.target.value)} min={getMinDate()} className="w-full pl-10 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none" data-id="element-5425" />
                  </div>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-5426">
                    Minim 3 zile de la primul apel
                  </p>
                </div>

                {/* Time Input */}
                <div data-id="element-5427">
                  <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-5428">
                    Ora Apelului *
                  </label>
                  <div className="relative" data-id="element-5429">
                    <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" data-id="element-5430" />
                    <input type="time" value={callTime} onChange={e => setCallTime(e.target.value)} className="w-full pl-10 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none" data-id="element-5431" />
                  </div>
                </div>

                {/* Notes */}
                <div data-id="element-5432">
                  <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-5433">
                    Notițe pentru Apel (opțional)
                  </label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Ex: Verifică dacă bugetul s-a schimbat, discută despre comision..." className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none" data-id="element-5434" />
                </div>

                {/* Warning Box */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-id="element-5435">
                  <p className="text-xs text-amber-900" data-id="element-5436">
                    <strong data-id="element-5437">⚠️ Important:</strong> După programare, lead-ul va
                    rămâne în status "{currentStatus}" până la următorul apel.
                    Vei primi o notificare în ziua programată.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-slate-50 px-6 py-4 flex items-center justify-between border-t border-slate-200" data-id="element-5438">
                <button onClick={onClose} className="px-5 py-2.5 rounded-xl font-semibold text-slate-700 hover:bg-slate-200 transition-colors" data-id="element-5439">
                  Anulează
                </button>
                <button onClick={handleSubmit} className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all" data-id="element-5440">
                  Programează Apel
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}